import { useState, useMemo, useRef } from "react";
import { useTranslate } from "@refinedev/core";

import { usePaginatedTable, useQueryParams } from "@shared/hooks";
import { ModalRef } from "@shared/components";
import { useFetchResourcesByIdsQuery } from "@shared/services/queries";
import { ApiResponse } from "@shared/types";
import { Book } from "@modules/books/types";
import { BookAuthor } from "@modules/bookAuthors/types";

export function useBookAuthors() {
  const [selectedRows, setSelectedRows] = useState<number[]>([]);

  const createBookAuthorModalRef = useRef<ModalRef>(null);
  const editBookAuthorModalRef = useRef<ModalRef>(null);

  const translate = useTranslate();
  const { getQueryParams } = useQueryParams();

  const { rows, loading, refetch, ...tableProps } = usePaginatedTable<BookAuthor>({
    resource: "v1/book_contributor_admin",
    filters: getQueryParams(),
    sorters: { ordering: "-id" },
  });

  const bookIds = useMemo(
    () => [...new Set(rows?.map((audioBook) => audioBook.book).filter((id): id is number => !!id))],
    [rows]
  );

  const { data: books } = useFetchResourcesByIdsQuery<ApiResponse<Book>>({
    resource: "v1/book_admin",
    ids: bookIds,
  });

  const dataToExport = useMemo(() => {
    return selectedRows.length > 0
      ? rows.filter((row) => selectedRows.includes(row.id))
      : rows;
  }, [selectedRows, rows]);

  return {
    rows,
    loading,
    tableProps,
    books: books?.results,
    selectedRows,
    dataToExport,
    createBookAuthorModalRef,
    editBookAuthorModalRef,
    refetch,
    setSelectedRows,
    translate,
  };
}
